import type { AudioClip, Project } from '@/domain/types';
import { clipEndTicks, tempoMapOf, warpBeats } from '@/domain/selectors';
import { beatToSourceSec, warpCurve } from '@/domain/warpMap';
import {
  rampAt,
  rampTicksInto,
  secondsIntoSegmentAt,
  ticksToSeconds,
  type NormalisedTempoMap,
  type TempoRampShapeData,
} from '@/timing/tempoMap';

/**
 * Bending a clip through a tempo change.
 *
 * A clip that follows the project tempo occupies a fixed number of beats. Where
 * the tempo is constant, those beats are a constant number of seconds each and
 * one stretch ratio fits the whole clip — that is the ordinary case, and it goes
 * through the plain per-asset render.
 *
 * Where the tempo *changes inside the clip*, no single ratio is right: the first
 * half needs one and the second another, and picking either puts the rest of the
 * clip in the wrong place. So the clip is cut into spans at every tempo event
 * and every warp marker, each with its own start and end in source and on the
 * timeline. A span under a held tempo is a straight line between those; a
 * span under a *ramp* carries the ramp itself, so the renderer's read position
 * follows the tempo's own curve through it rather than a chord across it. For
 * a while a ramp was stepped in quarter-second slices instead — each at one
 * ratio — which put a click a couple of milliseconds off its beat mid-ramp;
 * the curve is closed-form both ways, so there was never a reason to.
 *
 * Everything here is arithmetic over the tempo map; nothing touches audio.
 */

export interface WarpSpan {
  /** Offset into the clip's own source region, in source seconds. */
  sourceStartSec: number;
  sourceEndSec: number;
  /** Where this span begins on the timeline, in seconds from the clip's start. */
  outStartSec: number;
  outEndSec: number;
  /**
   * The tempo ramp this span lies under, when it lies under one: the source
   * moves through it as the ramp's beats do, not in a straight line.
   */
  ramp?: WarpSpanRamp;
}

export interface WarpSpanRamp extends TempoRampShapeData {
  ppq: number;
  /** Seconds from the ramp's own start to this span's start. */
  offsetSec: number;
  /** How many ticks the span covers, which is how far the ramp carries it. */
  spanTicks: number;
}

/**
 * How far through a span the source has moved at `outSec` seconds into it.
 * A straight line under a held tempo; the ramp's own curve under a ramp.
 */
export function spanProgress(span: WarpSpan, outSec: number): number {
  const outLength = Math.max(1e-9, span.outEndSec - span.outStartSec);
  const straight = Math.min(1, Math.max(0, outSec / outLength));
  if (!span.ramp) return straight;
  const ramp = span.ramp;
  const from = rampTicksInto(ramp, ramp.ppq, ramp.offsetSec);
  const to = rampTicksInto(ramp, ramp.ppq, ramp.offsetSec + Math.min(outLength, Math.max(0, outSec)));
  return Math.min(1, Math.max(0, (to - from) / Math.max(1e-9, ramp.spanTicks)));
}

export interface WarpPlan {
  spans: WarpSpan[];
  /** Total timeline length, in seconds. Equals the last span's end. */
  totalSec: number;
}

/**
 * Spans below this length are folded into their neighbour. WSOLA needs at least
 * a couple of analysis frames to work with, and a sliver of a span would be
 * resampled instead — an audible pitch blip at a bar line.
 */
const MIN_SPAN_SEC = 0.12;

/** Tick positions inside a clip where the tempo changes. */
function breakTicks(map: NormalisedTempoMap, startTick: number, endTick: number): number[] {
  return map.events.map((event) => event.tick).filter((tick) => tick > startTick && tick < endTick);
}

/**
 * How a following clip's source maps onto the timeline, span by span.
 *
 * Returns null when one ratio fits the whole clip — which is the common case,
 * and which the caller renders far more cheaply.
 */
export function planWarp(project: Project, clip: AudioClip): WarpPlan | null {
  const beats = warpBeats(clip);
  if (beats === null || clip.durationSec <= 0) return null;

  const map = tempoMapOf(project);
  const startTick = clip.startTicks;
  const endTick = clipEndTicks(project, clip);
  const ppq = project.transport.ppq;
  const curve = warpCurve(clip);

  // A break wherever the ratio changes: at every tempo event inside the clip,
  // and at every warp marker, which is the other thing that bends the map.
  const markerTicks = curve
    .slice(1, -1)
    .map((point) => startTick + point.beat * ppq)
    .filter((tick) => tick > startTick && tick < endTick);
  const breaks = [...breakTicks(map, startTick, endTick), ...markerTicks].sort((a, b) => a - b);
  // A clip lying wholly inside one ramp has no break in it and still bends.
  if (breaks.length === 0 && rampAt(map, startTick) === null) return null;

  const clipStartSec = ticksToSeconds(map, startTick);
  const sourceAt = (tick: number): number => beatToSourceSec(curve, (tick - startTick) / ppq);

  // Spans are tick ranges first, and slivers are folded as ranges: a span too
  // short to analyse would be resampled, and a pitch blip at a bar line is
  // worse than a slightly coarser fit. A sliver goes into whatever came before
  // it, or into what follows if it is the first.
  const points = [startTick, ...breaks, endTick];
  const ranges: { from: number; to: number }[] = [];
  for (let i = 0; i + 1 < points.length; i++) {
    const range = { from: points[i], to: points[i + 1] };
    const previous = ranges[ranges.length - 1];
    if (previous && ticksToSeconds(map, range.to) - ticksToSeconds(map, range.from) < MIN_SPAN_SEC) {
      previous.to = range.to;
      continue;
    }
    ranges.push(range);
  }
  while (ranges.length > 1 && ticksToSeconds(map, ranges[0].to) - ticksToSeconds(map, ranges[0].from) < MIN_SPAN_SEC) {
    ranges[1].from = ranges[0].from;
    ranges.shift();
  }

  // Absolute positions throughout, so rounding cannot accumulate across spans.
  // A span whose start lies under a ramp carries the ramp; a folded sliver
  // that crossed into a held tempo is then read as if the ramp continued,
  // which over a sliver is the same thing.
  const spans: WarpSpan[] = ranges.map(({ from, to }) => {
    const ramp = rampAt(map, from);
    return {
      sourceStartSec: sourceAt(from),
      sourceEndSec: sourceAt(to),
      outStartSec: ticksToSeconds(map, from) - clipStartSec,
      outEndSec: ticksToSeconds(map, to) - clipStartSec,
      ...(ramp ? { ramp: { ...ramp, ppq, offsetSec: secondsIntoSegmentAt(map, from), spanTicks: to - from } } : {}),
    };
  });
  if (spans.length < 2 && !spans.some((span) => span.ramp)) return null;

  return { spans, totalSec: spans[spans.length - 1].outEndSec };
}

/** A key that changes whenever anything about a clip's bend changes. */
export function warpKey(clip: AudioClip, plan: WarpPlan): string {
  const shape = plan.spans
    .map((s) => {
      const line = [s.sourceStartSec, s.sourceEndSec, s.outStartSec, s.outEndSec].map((v) => v.toFixed(4)).join(',');
      // A ramp bends the span between the same ends, so it is part of the shape.
      return s.ramp ? `${line}~${s.ramp.shape[0]}${s.ramp.b0.toFixed(3)}>${s.ramp.b1.toFixed(3)}@${s.ramp.offsetSec.toFixed(4)}` : line;
    })
    .join(';');
  // The spans already encode the markers, since a marker is a break with its own
  // ratio, so the shape is the whole identity of the render.
  return `${clip.assetId}#${clip.offsetSec.toFixed(6)}+${clip.durationSec.toFixed(6)}~${
    clip.preservePitch ? 'p' : 'v'
  }~${shape}`;
}
